import React, { useEffect, useRef, useState } from 'react';
import { Typography, Box, Container, useTheme } from '@mui/material';

const Section = ({ title, subtitle, hasBackground = false, children, id = null }) => {
  const theme = useTheme();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (sectionRef.current) {
            observer.unobserve(sectionRef.current);
          }
        }
      },
      {
        threshold: 0.08,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    const currentElem = sectionRef.current;
    if (currentElem) {
      observer.observe(currentElem);
    }

    return () => {
      if (currentElem) observer.unobserve(currentElem);
    };
  }, []);

  return (
    <Box
      id={id}
      ref={sectionRef}
      component="section"
      sx={{
        py: { xs: 6, md: 9 },
        backgroundColor: hasBackground
          ? theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.02)'
            : 'rgba(45, 52, 132, 0.02)'
          : 'transparent',
        borderTop: hasBackground
          ? theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.05)'
            : '1px solid rgba(45, 52, 132, 0.05)'
          : 'none',
        borderBottom: hasBackground
          ? theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.05)'
            : '1px solid rgba(45, 52, 132, 0.05)'
          : 'none',
        scrollMarginTop: '80px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
          transition: 'opacity 0.85s cubic-bezier(0.16, 1, 0.3, 1), transform 0.85s cubic-bezier(0.16, 1, 0.3, 1)',
          willChange: 'opacity, transform',
        }}
      >
        {title && (
          <Box sx={{ mb: { xs: 4, md: 6 }, textAlign: 'center' }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 900,
                color: theme.palette.text.primary,
                letterSpacing: '-0.02em',
                fontSize: { xs: '1.75rem', sm: '2.2rem', md: '2.5rem' },
                mb: 1,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  maxWidth: 680,
                  mx: 'auto',
                  fontSize: { xs: '0.95rem', md: '1.05rem' },
                  lineHeight: 1.6,
                }}
              >
                {subtitle}
              </Typography>
            )}
            <Box
              sx={{
                width: 50,
                height: 4,
                bgcolor: '#f15b28',
                borderRadius: '2px',
                mx: 'auto',
                mt: 2,
                transform: isVisible ? 'scaleX(1)' : 'scaleX(0)',
                transition: 'transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s',
              }}
            />
          </Box>
        )}

        {children}
      </Container>
    </Box>
  );
};

export default Section;
